import { Panel } from '@/components/panel/panel';
import { SelectedPaymentMethodDisplay } from '@/components/payment-methods/SelectedPaymentMethodDisplay';
import PayoutBatchStatusBadge from '@/components/payouts/PayoutBatchStatusBadge';
import Money from '@/components/text/money';
import { DataListItem, DataListRoot } from '@/components/ui/data-list';
import type { PaymentMethod } from '@/types/payment-method';
import type { Payout } from '@/types/payout';
import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

interface PayoutSummaryProps {
  payout: Payout | null;
  formatScheduledDate: () => string;
}

export const PayoutSummary: React.FC<PayoutSummaryProps> = ({ payout, formatScheduledDate }) => {
  const { t } = useTranslation();

  const summaryItems = useMemo(() => {
    if (!payout) return [];

    // Use backend-calculated VAT
    const vatAmount = payout.vatAmount?.amount ?? 0;

    // Calculate total amount paid (commissions + platform fee + VAT)
    const totalAmountPaid =
      payout.totalCommissionAmount.amount +
      (payout.totalCollectedPlatformFee?.amount ?? 0) +
      vatAmount;

    const items: Array<{ label: string; value: React.ReactNode }> = [
      {
        label: t('payouts.confirmation.summary.status'),
        value: (
          <PayoutBatchStatusBadge
            status={payout.status}
            fundedAt={payout.fundedAt}
            lastPaymentFailedAt={payout.lastPaymentFailedAt}
            closedReason={payout.closedReason}
          />
        ),
      },
    ];

    if (payout.paymentMethod) {
      items.push({
        label: t('payouts.confirmation.summary.paymentMethod'),
        value: (
          <SelectedPaymentMethodDisplay paymentMethod={payout.paymentMethod as PaymentMethod} />
        ),
      });
    }

    items.push({
      label: t('payouts.confirmation.summary.payoutDate'),
      value: formatScheduledDate(),
    });

    items.push({
      label: t('payouts.confirmation.summary.totalCommissions'),
      value: (
        <Money
          fontSize="sm"
          value={payout.totalCommissionAmount.amount}
          currency={payout.totalCommissionAmount.currencyCode}
        />
      ),
    });

    if (payout.totalCollectedPlatformFee) {
      items.push({
        label: t('payouts.confirmation.summary.platformFee'),
        value: (
          <Money
            fontSize="sm"
            value={payout.totalCollectedPlatformFee.amount}
            currency={payout.totalCollectedPlatformFee.currencyCode}
          />
        ),
      });
    }

    items.push({
      label: t('payouts.confirmation.summary.vat'),
      value: (
        <Money
          fontSize="sm"
          value={vatAmount}
          currency={payout.totalCommissionAmount.currencyCode}
        />
      ),
    });

    items.push({
      label: t('payouts.confirmation.summary.totalAmountPaid'),
      value: (
        <Money
          fontSize="sm"
          value={totalAmountPaid}
          currency={payout.totalCommissionAmount.currencyCode}
        />
      ),
    });

    return items;
  }, [payout, formatScheduledDate, t]);

  if (!payout) return null;

  return (
    <Panel
      title={t('payouts.confirmation.summary.title')}
      animation="fadeIn 0.5s ease-out 0.2s both"
      css={{
        '@keyframes fadeIn': {
          from: { opacity: 0, transform: 'translateY(10px)' },
          to: { opacity: 1, transform: 'translateY(0)' },
        },
      }}
    >
      <DataListRoot orientation="horizontal" divideY="1px" gapY={1}>
        {summaryItems.map((item, index) => (
          <DataListItem
            key={`${item.label}-${index}`}
            label={item.label}
            value={item.value}
            py={2}
            itemValueProps={{ justifyContent: 'flex-end' }}
          />
        ))}
      </DataListRoot>
    </Panel>
  );
};
